import { useRef, useState } from "react";

import uploadIcon from "../assets/upload-green-icon.svg";
import infoIcon from "../assets/info-icon.svg";
import blackDeleteIcon from "../assets/black-delete-icon.svg";
import saveIcon from "../assets/save-icon.svg";
import whiteRightIcon from "../assets/white-right-icon.svg";

const NAME_TRUNCATE_LENGTH = 48;
const QUANTITY_OPTIONS = Array.from({ length: 10 }, (_, index) =>
  String(index + 1),
);

const IMPLANT_OPTIONS = [
  "Coronary Stent",
  "Drug Eluting Stent",
  "Bare Metal Stent",
  "No. of Stents",
  "Pacemaker",
  "Intraocular Lens",
  "Surgical Mesh",
  "Hip Implant",
  "Knee Implant",
  "Bone Screw",
  "Bone Plate",
  "Intramedullary Nail",
  "Spinal Cage",
  "Heart Valve",
  "Cochlear Implant",
  "Vascular Graft",
  "Other",
];

const gridColsClass =
  "grid grid-cols-[28px_minmax(0,2.2fr)_minmax(0,1.35fr)_58px_minmax(90px,1fr)] gap-x-[12px] xl:gap-x-[28px] 2xl:gap-x-[36px] 3xl:grid-cols-[40px_minmax(0,2.4fr)_minmax(0,1.4fr)_70px_minmax(120px,1fr)] 3xl:gap-x-[24px] items-start";

const ChevronIcon = () => (
  <div className="shrink-0 pointer-events-none">
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
      <path
        d="M7 10.5L14 17.5L21 10.5"
        stroke="#5AC69F"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const NameCell = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const value = text || "--";
  const shouldTruncate = value.length > NAME_TRUNCATE_LENGTH;
  const displayText =
    !shouldTruncate || expanded
      ? value
      : `${value.slice(0, NAME_TRUNCATE_LENGTH)}...`;

  return (
    <div className="min-w-0 pr-[12px] break-words font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000]">
      {displayText}
      {shouldTruncate && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="ml-[4px] bg-transparent border-none p-0 font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#5AC69F] cursor-pointer"
        >
          {expanded ? "show less" : "show more"}
        </button>
      )}
    </div>
  );
};

const ImplantsFormAi = () => {
  const [implants, setImplants] = useState([]);
  const [implantName, setImplantName] = useState("");
  const [customImplantName, setCustomImplantName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [isEditMode, setIsEditMode] = useState(true);
  const nextIdRef = useRef(1);

  const resolvedImplantName =
    implantName === "Other" ? customImplantName.trim() : implantName.trim();

  const resetAddForm = () => {
    setImplantName("");
    setCustomImplantName("");
    setQuantity("");
  };

  const handleAdd = () => {
    if (!resolvedImplantName || !quantity) return;

    setImplants((prev) => [
      ...prev,
      {
        id: nextIdRef.current,
        name: resolvedImplantName,
        quantity,
        file: null,
      },
    ]);
    nextIdRef.current += 1;
    resetAddForm();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleFileChange = (id, file) => {
    if (!file) return;
    setImplants((prev) =>
      prev.map((item) => (item.id === id ? { ...item, file } : item)),
    );
  };

  const handleDrop = (id, e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    handleFileChange(id, file);
  };

  const handleDelete = (id) => {
    setImplants((prev) => prev.filter((item) => item.id !== id));
  };

  return (
    <div className="w-full">
      <div className="w-full h-0 border border-solid border-[#00000010] bg-[#00000010]" />{" "}
      <div className="flex items-center justify-between gap-3 pt-[30px]">
        <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
          <div className="relative flex-1 min-w-0 max-w-[280px] box-border overflow-hidden h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent">
            <select
              value={implantName}
              onChange={(e) => setImplantName(e.target.value)}
              className="absolute inset-0 w-full h-full pl-[32px] pr-[48px] bg-transparent border-none outline-none focus:outline-none focus:ring-0 appearance-none cursor-pointer font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B]"
            >
              <option value="" disabled hidden>
                Select
              </option>
              {IMPLANT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <div className="absolute right-[14px] top-1/2 -translate-y-1/2 pointer-events-none">
              <ChevronIcon />
            </div>
          </div>

          <div className="relative flex-1 min-w-0 max-w-[280px] box-border overflow-hidden h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent">
            <select
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="absolute inset-0 w-full h-full pl-[32px] pr-[48px] bg-transparent border-none outline-none focus:outline-none focus:ring-0 appearance-none cursor-pointer font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B]"
            >
              <option value="" disabled hidden>
                Quantity
              </option>
              {QUANTITY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <div className="absolute right-[14px] top-1/2 -translate-y-1/2 pointer-events-none">
              <ChevronIcon />
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handleAdd}
          className="shrink-0 w-[88px] xl:w-[155px] h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent flex justify-center items-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B]"
        >
          ADD
        </button>
      </div>
      {implantName === "Other" && (
        <div className="relative w-[280px] box-border overflow-hidden h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent px-[32px] flex items-center mt-[8px]">
          <input
            type="text"
            placeholder="Enter implant name"
            value={customImplantName}
            onChange={(e) => setCustomImplantName(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full min-w-0 bg-transparent border-none outline-none focus:outline-none focus:ring-0 focus:border-none shadow-none font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#1A1A1A] placeholder:text-[#8B8B8B]"
          />
        </div>
      )}
      <div className="pt-[42px]">
        <div className={gridColsClass}>
          <div className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            No.
          </div>
          <div className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            Name
          </div>
          <div className=" font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            Upload
          </div>
          <div className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            Quantity
          </div>
          <div className="justify-self-end font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            Action
          </div>
        </div>

        <div className="mt-[34px] flex flex-col gap-y-[34px]">
          {implants.map((item, index) => (
            <div key={item.id} className={gridColsClass}>
              <div className="font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000]">
                {index + 1}.
              </div>

              <NameCell text={item.name} />

              <div
                className="flex items-center gap-[6px] min-w-0"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(item.id, e)}
              >
                <label
                  htmlFor={`implant-file-${item.id}`}
                  className="flex items-center gap-[6px] min-w-0 cursor-pointer"
                >
                  <img src={uploadIcon} alt="upload" className="shrink-0" />
                  <span className="underline font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000] truncate">
                    {item.file?.name || "Drag your file here or Browse"}
                  </span>
                </label>
                <img src={infoIcon} alt="info" className="shrink-0" />
                <input
                  id={`implant-file-${item.id}`}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    handleFileChange(item.id, e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </div>

              <div className="font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000]">
                {item.quantity}
              </div>

              <button
                type="button"
                onClick={() => handleDelete(item.id)}
                className="flex items-center justify-self-end bg-transparent border-none cursor-pointer p-0"
              >
                <img src={blackDeleteIcon} alt="delete icon" />
              </button>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end items-center gap-[10px] mt-[12px]">
        {isEditMode ? (
          <button
            type="button"
            onClick={() => setIsEditMode(false)}
            className="w-[170px] h-[74px] xl:w-[94.97px] xl:h-[40.97px] 2xl:w-[94.97px] 2xl:h-[40.97px] border border-none rounded-full bg-black flex justify-center items-center gap-[8px] font-lufga text-[14px] text-[#68FF9F] font-normal"
          >
            SAVE
            <img src={saveIcon} alt="save" className="pl-[16px]" />
          </button>
        ) : (
          <button
            type="button"
            className="w-[170px] h-[74px] xl:w-[101px] xl:h-[42px] 2xl:w-[101px] 2xl:h-[42px] rounded-full border border-none bg-[#5AC69F] flex justify-center items-center gap-[10px] font-lufga text-[14px] text-white font-normal"
          >
            SAVED
            <img src={whiteRightIcon} alt="white icon" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ImplantsFormAi;
